<template>
  <div class="legal-page">
    <Navigation />
    <div class="container">
      <h1>Terms & Conditions</h1>
      <p class="last-updated">Last updated: January 2026</p>

      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          These Terms of Service ("Agreement") constitute a legally binding contract between you ("User") and Renants 
          ("Platform"). By accessing, visiting, registering with, or using Renants and its services, you automatically 
          accept and agree to be bound by these terms. If you do not accept these terms, you are prohibited from using Renants.
        </p>
      </section>

      <section>
        <h2>2. Eligibility</h2>
        <p>
          Renants is offered only to users who are eighteen (18) years of age or older. By using Renants, you represent 
          and warrant that you meet these eligibility requirements and agree to comply with all applicable laws, rules, 
          and regulations.
        </p>
      </section>

      <section>
        <h2>3. Renants Platform and Services</h2>
        <p>
          Renants is an online classified advertisement and social networking platform that enables users to post, browse, 
          and respond to classified ads, listings, and social content. Users can post advertisements for services, products, 
          jobs, rentals, events, and other categories. Renants provides the technology platform and tools but does not create, 
          verify, endorse, or control user-generated content, listings, or advertisements.
        </p>
        <p>
          <strong>Renants is not responsible for:</strong>
        </p>
        <ul>
          <li>The accuracy, legality, or quality of listings, ads, or user-generated content</li>
          <li>The identity, credentials, or behavior of users posting or responding to ads</li>
          <li>Transactions, communications, or arrangements made between users</li>
          <li>The performance, delivery, or quality of goods or services advertised</li>
          <li>Disputes, damages, or losses arising from user interactions or transactions</li>
        </ul>
        <p>
          Your relationship with Renants is contractual in nature only. Renants acts solely as a venue for users to connect.
        </p>
      </section>

      <section>
        <h2>4. User Accounts and Responsibilities</h2>
        <p>
          To access certain features of Renants, you must create a user account. You are solely responsible for all 
          activity that occurs under your account. You must keep your login credentials confidential and notify us 
          immediately of any unauthorized access. You must maintain accurate and up-to-date information in your account.
        </p>
      </section>

      <section>
        <h2>5. Disclaimers and "As Is" Basis</h2>
        <p>
          Your use of Renants is at your own risk. The platform and all services, information, and content offered through 
          it are provided on an "as is" and "as available" basis with all faults. Renants makes no warranties of any kind, 
          whether express or implied, including warranties of merchantability, fitness for a particular purpose, title, 
          non-infringement, or that the platform will be uninterrupted or error-free.
        </p>
      </section>

      <section>
        <h2>6. Limitation of Liability</h2>
        <p>
          In no event shall Renants or its officers, directors, employees, agents, or service providers be liable for any 
          direct, indirect, incidental, special, or consequential damages (including lost profits, loss of data, or business 
          interruption) arising from your use of or inability to use the platform or services, even if Renants has been 
          notified of the possibility of such damages.
        </p>
      </section>

      <section>
        <h2>7. Payments and Fees</h2>
        <p>
          All transactions on Renants are final and non-refundable unless otherwise expressly stated by a Provider. You are 
          responsible for all payment processing fees, service fees, taxes, duties, and other charges associated with your 
          transactions. Renants uses third-party payment processors and is not responsible for payment processing errors or issues.
        </p>
      </section>

      <section>
        <h2>8. User Content and Listings</h2>
        <p>
          When you post ads, listings, or content on Renants, you grant Renants a worldwide, non-exclusive, royalty-free 
          license to use, display, reproduce, modify, and distribute your content for operating and promoting the platform. 
          You retain ownership of your content but acknowledge that it will be publicly visible to other users.
        </p>
        <p>
          <strong>You represent and warrant that:</strong>
        </p>
        <ul>
          <li>You own or have rights to all content you post</li>
          <li>Your listings and ads are accurate, truthful, and not misleading</li>
          <li>Your content does not violate any laws or third-party rights</li>
          <li>You have authority to sell, rent, or offer any items or services you advertise</li>
          <li>You will honor all commitments made in your listings</li>
        </ul>
      </section>

      <section>
        <h2>9. Prohibited Content and Activities</h2>
        <p>
          You may not post, transmit, or engage in the following on Renants:
        </p>
        <ul>
          <li>Fraudulent, misleading, or deceptive advertisements or listings</li>
          <li>Illegal goods, services, or activities (drugs, weapons, stolen goods, etc.)</li>
          <li>Adult content, escort services, or sexually explicit material</li>
          <li>Spam, duplicate postings, or excessive commercial solicitation</li>
          <li>Pyramid schemes, multi-level marketing, or get-rich-quick schemes</li>
          <li>Impersonation, fake accounts, or misrepresentation of identity</li>
          <li>Hate speech, harassment, threats, or discriminatory content</li>
          <li>Infringement of intellectual property, copyright, or trademark rights</li>
          <li>Scams, phishing attempts, or requests for money transfers</li>
          <li>Personal information of others without their consent</li>
          <li>Systematic scraping, data mining, or automated posting</li>
          <li>Any activity that violates local, state, or federal laws</li>
        </ul>
      </section>

      <section>
        <h2>10. Content Moderation and Removal</h2>
        <p>
          Renants reserves the right (but has no obligation) to review, monitor, edit, or remove any user content, listing, 
          or advertisement at any time, with or without notice, for any reason, including violation of these terms or if 
          deemed inappropriate, illegal, or harmful. Renants may also suspend or terminate accounts that repeatedly violate 
          these terms.
        </p>
      </section>

      <section>
        <h2>11. Safety and Scam Prevention</h2>
        <p>
          <strong>User Safety Guidelines:</strong>
        </p>
        <ul>
          <li>Always meet in public places for in-person transactions</li>
          <li>Never send money via wire transfer, money order, or prepaid cards to unknown parties</li>
          <li>Be cautious of deals that seem too good to be true</li>
          <li>Verify the identity and legitimacy of other users before transacting</li>
          <li>Report suspicious listings, scams, or fraudulent activity immediately</li>
          <li>Do not share sensitive personal or financial information publicly</li>
        </ul>
        <p>
          Renants provides safety information and scam alerts but cannot guarantee user safety. You are solely responsible 
          for your interactions with other users and transactions conducted through the platform.
        </p>
      </section>

      <section>
        <h2>12. Modifications and Termination</h2>
        <p>
          Renants reserves the right to modify these terms at any time. Modified terms become effective upon posting on 
          the platform. Your continued use of Renants constitutes acceptance of modified terms. Renants also reserves the 
          right to suspend or terminate your account and access to the platform at any time, with or without notice, for 
          violation of these terms or for any other reason at Renants' sole discretion.
        </p>
      </section>

      <section>
        <h2>13. Governing Law and Disputes</h2>
        <p>
          These terms and all disputes arising from them are governed by the laws of the United States. You irrevocably 
          agree to submit to the exclusive jurisdiction of the courts in the United States for resolution of any disputes, 
          claims, or legal proceedings related to your use of Renants.
        </p>
      </section>

      <section>
        <h2>14. Contact Information</h2>
        <p>
          If you have any questions about these Terms & Conditions, please contact us at:
        </p>
        <p>
          <strong>Email:</strong> <a href="mailto:info@renants.com">info@renants.com</a>
        </p>
      </section>
    </div>
    <Footer />
  </div>
</template>

<script>
import Navigation from './Navigation.vue';
import Footer from './footer.vue';

export default {
  name: 'TermsAndConditions',
  components: {
    Navigation,
    Footer
  }
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 60px 32px;
  flex: 1;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  color: #333333;
  line-height: 1.8;
}

h1 {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1a1a1a;
}

.last-updated {
  font-size: 14px;
  color: #333333;
  margin-bottom: 40px;
  font-style: italic;
}

section {
  margin-bottom: 40px;
}

h2 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #2c2c2c;
  margin-top: 32px;
}

p {
  font-size: 16px;
  margin-bottom: 16px;
  text-align: justify;
}

ul {
  margin-left: 32px;
  margin-bottom: 16px;
}

li {
  font-size: 16px;
  margin-bottom: 12px;
  text-align: justify;
}

a {
  color: #3467d5;
  text-decoration: none;
  transition: color 0.3s ease;
}

a:hover {
  color: #2651b1;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .container {
    padding: 40px 24px;
  }

  h1 {
    font-size: 32px;
  }

  h2 {
    font-size: 20px;
  }

  p,
  li {
    font-size: 15px;
    text-align: left;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 24px 16px;
  }

  h1 {
    font-size: 24px;
  }

  h2 {
    font-size: 18px;
  }

  p,
  li {
    font-size: 14px;
  }
}
</style>
